<template>
  <div class="flex flex-col gap-2 w-full">
    <div class="flex gap-2 w-full items-start">
      <div class="relative flex-1">
        <VInput
          v-model="currentSearchTerm"
          type="text"
          placeholder="Invite users via email"
          :disabled="loading"
          class="w-full"
          @click="handleInputClick"
          @blur="handleInputBlur"
        />
        <div
          v-if="isSearchOpen"
          class="absolute z-10 w-full mt-1 bg-white border border-gray-600 rounded-md shadow-sm max-h-96 overflow-y-auto"
        >
          <button
            v-for="user in searchResults"
            :key="user.id"
            class="flex items-center gap-2 px-4 py-2 text-sm cursor-pointer w-full text-left hover_bg-gray-100"
            @click="selectUser(user)"
          >
            <div class="flex flex-col">
              <span>{{ user.name }}</span>
              <span v-if="user.email_address" class="text-gray-500 text-xs">{{ user.email_address }}</span>
            </div>
          </button>
        </div>
      </div>
      <VButton
        size="md"
        :disabled="loading"
        :aria-label="loading ? 'Inviting users...' : 'Invite selected users'"
        @click="handleInviteUser"
      >
        Invite
      </VButton>
    </div>

    <div
      v-if="showExternalUserNotice"
      class="flex items-center gap-2 text-sm text-gold-900"
      role="status"
    >
      <FontAwesomeIcon icon="user" class="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span>External user - view only</span>
    </div>

    <div
      v-if="notification.show"
      :class="[
        'flex gap-2 items-start px-4 py-2 rounded-md text-sm border',
        notification.type === 'danger' ? 'bg-red-100 text-red-800 border-red-200' : '',
        notification.type === 'warning' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : '',
        notification.type === 'success' ? 'bg-green-100 text-green-800 border-green-200' : '',
      ]"
      :aria-live="isSaveReminderNotice(notification) ? 'polite' : undefined"
      role="status"
    >
      <FontAwesomeIcon
        v-if="notification.type === 'success'"
        icon="circle-check"
        class="w-4 h-4 shrink-0 mt-0.5 text-green-700"
        aria-hidden="true"
      />
      <div v-if="isSaveReminderNotice(notification)" class="min-w-0 leading-snug">
        {{ notification.text }}<span class="font-semibold">Save Changes</span>.
      </div>
      <div v-else class="min-w-0 leading-snug">
        <div class="font-medium">{{ notification.title }}</div>
        <div v-if="notification.text">{{ notification.text }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import debounce from '~~/lib/debounce'
import type { User as APIUser } from '~~/entities/user'
import { validate as validateEmail } from 'email-validator'
import * as Sentry from '@sentry/vue'
import type { DeepReadonly } from 'vue'
import type { EditDeck } from '~/composables/useEditDeck'

interface InviteTarget {
  type: 'internal' | 'external'
  email: string
  user?: Pick<APIUser, 'id' | 'name' | 'email_address'>
}

const loading = defineModel<boolean>('loading', { required: true })

const props = defineProps<{
  deck: DeepReadonly<EditDeck>
  existingUsers?: { user_id?: number | null; email?: string; name?: string | null; is_pending?: boolean }[]
}>()

const emit = defineEmits<{
  inviteRequested: [invite: InviteTarget]
}>()

const { $analytics } = useNuxtApp()

const searchResults = ref<APIUser[]>([])
const currentSearchTerm = ref('')
const selectedUser = ref<APIUser | null>(null)
type ToastKind = 'success' | 'danger' | 'warning'

/** Save reminder uses `success` with an empty title and lead-in copy in `text`. */
function isSaveReminderNotice(n: { type: string; title: string }) {
  return n.type === 'success' && n.title === ''
}

const notification = ref({
  show: false,
  type: '' as ToastKind | '',
  title: '',
  text: '',
})

interface PendingInviteEntry {
  email: string
  isExternal: boolean
}

const pendingInviteEntries = computed((): PendingInviteEntry[] => {
  const rows = props.existingUsers?.filter(u => u.is_pending) ?? []
  return rows.map(u => {
    const email = (u.email || '').trim()
    const isExternal = u.user_id == null
    return { email: email || '(unknown)', isExternal }
  })
})

const lastInvitedForBanner = ref<PendingInviteEntry | null>(null)

const bannerPendingInviteEntries = computed((): PendingInviteEntry[] => {
  const pending = pendingInviteEntries.value
  const last = lastInvitedForBanner.value
  if (!last) return pending

  const lastEmail = last.email.toLowerCase()
  return pending.filter(e => e.email.toLowerCase() === lastEmail)
})

watch(
  pendingInviteEntries,
  pending => {
    const last = lastInvitedForBanner.value
    if (!last) return
    const stillPending = pending.some(e => e.email.toLowerCase() === last.email.toLowerCase())
    if (!stillPending) lastInvitedForBanner.value = null
  },
  { deep: true },
)

const pendingSaveInviteBanner = computed(() => {
  const entries = bannerPendingInviteEntries.value
  if (!entries.length) return null

  const list = entries.map(e => e.email).join(', ')
  const n = entries.length
  const anyExternal = entries.some(e => e.isExternal)
  const anyInternal = entries.some(e => !e.isExternal)
  const allExternal = anyExternal && !anyInternal
  const allInternal = anyInternal && !anyExternal

  if (n === 1) {
    const e = entries[0]!
    if (e.isExternal) {
      return `${e.email} has been added. A 7-day access link will be sent after you click `
    }
    return `${e.email} has been added. They will be granted access after you click `
  }

  if (allExternal) {
    return `${list} have been added. 7-day access links will be sent after you click `
  }

  if (allInternal) {
    return `${list} have been added. They will be granted access after you click `
  }

  return `${list} have been added. 7-day access links will be sent to external invitees, and company users will be granted access, after you click `
})

const BANNER_AUTO_DISMISS_MS = 5000

let notificationDismissTimeout: ReturnType<typeof setTimeout> | null = null

function clearNotificationDismissTimer() {
  if (notificationDismissTimeout != null) {
    clearTimeout(notificationDismissTimeout)
    notificationDismissTimeout = null
  }
}

function finishNotificationDismiss() {
  notificationDismissTimeout = null
  const wasSaveReminder = isSaveReminderNotice(notification.value)
  notification.value.show = false

  const pendingText = pendingSaveInviteBanner.value
  if (pendingText && !wasSaveReminder) {
    Object.assign(notification.value, {
      show: true,
      type: 'success',
      title: '',
      text: pendingText,
    })
    notificationDismissTimeout = setTimeout(finishNotificationDismiss, BANNER_AUTO_DISMISS_MS)
  } else {
    Object.assign(notification.value, { type: '', title: '', text: '' })
  }
}

function scheduleNotificationDismiss() {
  clearNotificationDismissTimer()
  notificationDismissTimeout = setTimeout(finishNotificationDismiss, BANNER_AUTO_DISMISS_MS)
}

watch(
  pendingSaveInviteBanner,
  text => {
    if (!text) {
      if (isSaveReminderNotice(notification.value)) {
        clearNotificationDismissTimer()
        notification.value.show = false
        Object.assign(notification.value, { type: '', title: '', text: '' })
      }
      return
    }
    clearNotificationDismissTimer()
    Object.assign(notification.value, { show: true, type: 'success', title: '', text })
    scheduleNotificationDismiss()
  },
  { immediate: true },
)

const showDropdown = ref(false)
const isSearchOpen = computed(() => searchResults.value.length > 0 && !loading.value && showDropdown.value)
const trimmedSearchTerm = computed(() => currentSearchTerm.value.trim())
const normalizedSearchEmail = computed(() => trimmedSearchTerm.value.toLowerCase())

const showExternalUserNotice = computed(() => {
  const email = normalizedSearchEmail.value
  if (!email || !validateEmail(email)) return false
  return selectedUser.value == null
})

function selectUser(user: APIUser) {
  selectedUser.value = user
  currentSearchTerm.value = user.email_address || user.name || ''
  closeSearch()
}

function showInlineMessage(type: ToastKind, title: string, text: string = '') {
  clearNotificationDismissTimer()
  Object.assign(notification.value, { show: true, type, title, text })
  scheduleNotificationDismiss()
}

onUnmounted(() => {
  clearNotificationDismissTimer()
})

async function handleInviteUser() {
  const email = normalizedSearchEmail.value

  if (selectedUser.value) {
    $analytics.track('deckShareInviteUser', { type: 'internal' })

    if (isUserAlreadyInvited(selectedUser.value)) {
      showInlineMessage('warning', 'User Already Invited', `${selectedUser.value.name} is already in the presentation permissions.`)
      return
    }

    const inviteEmail = selectedUser.value.email_address || email
    lastInvitedForBanner.value = { email: inviteEmail, isExternal: false }

    emit('inviteRequested', {
      type: 'internal',
      email: inviteEmail,
      user: {
        id: selectedUser.value.id,
        name: selectedUser.value.name,
        email_address: selectedUser.value.email_address,
      },
    })

    resetInput()
    return
  }

  if (!email) {
    showInlineMessage('danger', 'Email Required', 'Enter an email address to invite a user.')
    return
  }

  if (!validateEmail(email)) {
    showInlineMessage('danger', 'Invalid Email', 'Enter a valid email address to invite an external user.')
    return
  }

  if (isEmailAlreadyInvited(email)) {
    showInlineMessage('warning', 'User Already Invited', `${email} is already in the presentation permissions.`)
    return
  }

  $analytics.track('deckShareInviteUser', { type: 'external' })
  lastInvitedForBanner.value = { email, isExternal: true }
  emit('inviteRequested', { type: 'external', email })
  resetInput()
}

function isUserAlreadyInvited(user: APIUser): boolean {
  if (!props.existingUsers) return false

  return props.existingUsers.some(
    existingUser =>
      existingUser.user_id === user.id ||
      (existingUser.email && user.email_address && existingUser.email.toLowerCase() === user.email_address.toLowerCase()),
  )
}

function isEmailAlreadyInvited(email: string): boolean {
  if (!props.existingUsers) return false

  return props.existingUsers.some(existingUser => existingUser.email?.toLowerCase() === email)
}

function resetInput() {
  currentSearchTerm.value = ''
  selectedUser.value = null
  closeSearch()
}

async function performUserSearch() {
  const term = trimmedSearchTerm.value
  if (!term) {
    if (showDropdown.value) {
      await loadInitialUsers()
    } else {
      closeSearch()
    }
    return
  }

  try {
    const response = await $fetch<{ results: APIUser[] }>(`/v2/companies/${props.deck.company_id}/admins`, {
      ...useFetchSharedOptions(),
      query: { query: term },
    })
    const filteredResults = filterExistingUsers(response.results || [])
    searchResults.value = filteredResults

    // Auto-select user if email matches exactly
    const exactEmailMatch = filteredResults.find(user => user.email_address && user.email_address.toLowerCase() === term.toLowerCase())
    if (exactEmailMatch) {
      selectedUser.value = exactEmailMatch
    } else {
      selectedUser.value = null
    }
  } catch (error) {
    searchResults.value = []
    selectedUser.value = null
    Sentry.captureException(error)
  }
}

function closeSearch() {
  searchResults.value = []
  showDropdown.value = false
}

async function handleInputClick() {
  showDropdown.value = true
  if (currentSearchTerm.value.trim()) {
    await performUserSearch()
  } else {
    // Load initial users when clicking with no search term
    await loadInitialUsers()
  }
}

function handleInputBlur() {
  // Use setTimeout to allow click events on dropdown items to fire first
  setTimeout(() => {
    closeSearch()
  }, 150)
}

async function loadInitialUsers() {
  try {
    const response = await $fetch<{ results: APIUser[] }>(`/v2/companies/${props.deck.company_id}/admins`, {
      ...useFetchSharedOptions(),
    })
    searchResults.value = filterExistingUsers(response.results || [])
  } catch (error) {
    searchResults.value = []
    Sentry.captureException(error)
  }
}

function filterExistingUsers(users: APIUser[]): APIUser[] {
  if (!props.existingUsers) return users

  const existingUserIds = new Set(props.existingUsers.map(u => u.user_id).filter((id): id is number => typeof id === 'number'))
  const existingEmails = new Set(props.existingUsers.map(u => u.email?.toLowerCase()).filter(Boolean))

  return users.filter(user => {
    return !existingUserIds.has(user.id) && (!user.email_address || !existingEmails.has(user.email_address.toLowerCase()))
  })
}

watch(currentSearchTerm, debounce(performUserSearch, 250))
</script>
